import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { Category } from 'src/app/models/category.model';
import { GraphCategoryService } from 'src/app/services/graph/category.service';
import { GraphGlossaryService } from 'src/app/services/graph/glossary.service';
import { GraphReportLikeService } from 'src/app/services/graph/report-like.service';
import { ReportViewModel } from 'src/app/view-models/report-view-model';
import { ThemeService } from './../../shared/services/theme.service';

export interface SortBy {
	value: string;
	viewValue: string;
}

@Component({
	selector: 'app-category',
	templateUrl: './category.component.html',
	styleUrls: ['./category.component.scss']
})
export class CategoryComponent implements OnInit, AfterViewInit {
	public reports: ReportViewModel[] = [];
	public category: Observable<Category> = EMPTY;
	public categoryNotFound = false;

	public categoryKey: string = '';
	protected currentLanguageCode: string = 'en';
	protected isBetaReportsPage: boolean = false;

	constructor(
		@Inject(DOCUMENT) private readonly document: Document,
		private readonly glossaryService: GraphGlossaryService,
		private readonly reportLikeService: GraphReportLikeService,
		readonly categoryService: GraphCategoryService,
		private activatedRouter: ActivatedRoute,
		public themeService: ThemeService,
		private translateService: TranslateService
	) {}

	ngOnInit(): void {
		this.currentLanguageCode = this.translateService.currentLang ?? 'en';

		this.translateService.onLangChange.subscribe((params: LangChangeEvent) => {
			this.currentLanguageCode = params.lang ?? 'en';
			this.getReportsAndCategory();
		});

		this.getReportsAndCategory();
	}

	getReportsAndCategory() {
		this.categoryKey = this.activatedRouter.snapshot.params.categoryKey;
		this.isBetaReportsPage = this.categoryKey.toLowerCase() === 'beta';
		this.categoryNotFound = false;

		this.reportLikeService.reports
			.pipe(map(reps => reps.filter(x => (x.lang?.toLowerCase() === this.currentLanguageCode.toLowerCase()) 
				&& (this.isBetaReportsPage 
					? x.status?.toLowerCase() === 'beta'
					: x.categoryKeys.indexOf(this.categoryKey) > -1 )
		))).subscribe(r => this.reports = r);

		if (this.isBetaReportsPage) {
			this.category = of(new Category("Beta",
				this.translateService.instant("Home.BetaReports.Title"),
				this.translateService.instant("Home.BetaReports.Text"),
			));
		} else {
			const category$ = this.categoryService.getCategories().pipe(
				map(x => x.filter((y): y is Category => y.key?.toLowerCase() === this.categoryKey.toLowerCase() && y.languageCode?.toLowerCase() === this.currentLanguageCode.toLowerCase())[0]),
				shareReplay(1)
			);
			category$.subscribe(cat => this.categoryNotFound = !cat);
			this.category = category$;
		}
	}

	ngAfterViewInit() {
		if (this.isBetaReportsPage) {
			this.skipToMainContent();
		}
	}

	skipToMainContent() {
		let htmlElement = this.document.getElementById('maincontent');
		htmlElement?.focus();
		htmlElement?.scrollIntoView();
	}
}